// React and related imports
import React, { useEffect } from 'react';

// i18 Translation
import { TFunction } from 'i18next';

// Types and Interfaces
import { ContentState } from '../../hooks/useContentState';
import { IMultiLanguageValue } from '../../common/interfaces/MultiLanguageValue.interface';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

// Custom Components
import { Label, Card } from 'flowbite-react';
import { LanguageDropdown } from '../Shared/LanguageSelectionDropdown/LanguageDropdown';
import MDEditor, { commands } from '@uiw/react-md-editor';
import { useMarkdownImageUpload } from '../../hooks/useMarkdownImageUpload';
import {
  MultiLanguageField,
  useMultiLanguageInput,
} from '../Shared/MultiLanguageInput';
import { colors } from '../../common/constants/designTokens';

interface MarkdownEditorWithUploadProps {
  value: string;
  onChange: (v: string) => void;
  languageCode: string;
  isDarkMode: boolean;
}

const MarkdownEditorWithUpload: React.FC<MarkdownEditorWithUploadProps> = ({
  value,
  onChange,
  languageCode,
  isDarkMode,
}) => {
  const { textareaProps } = useMarkdownImageUpload({ value, onChange });
  return (
    <MDEditor
      id="markdownText-input"
      className={`custom-md-editor ${languageCode === 'ar' ? 'rtl' : 'ltr'} mt-1`}
      style={{
        color: isDarkMode ? colors.primary[50] : colors.primary[900],
        backgroundColor: isDarkMode ? colors.primary[900] : colors.primary[50],
      }}
      preview="edit"
      commands={[
        commands.bold,
        commands.italic,
        commands.strikethrough,
        commands.divider,
        commands.link,
        commands.quote,
        commands.unorderedListCommand,
        commands.orderedListCommand,
        commands.codePreview,
      ]}
      textareaProps={textareaProps}
      value={value}
      onChange={(newValue) => onChange(newValue as string)}
    />
  );
};

interface MultiLangContentInputsProps {
  t: TFunction<'translation', undefined>;
  contentState: ContentState;
  updateField: (
    field: keyof ContentState,
    value: IMultiLanguageValue[]
  ) => void;
  validateField: (field: string, value: string) => void;
}

export const MultiLangContentInputs: React.FC<MultiLangContentInputsProps> = ({
  t,
  contentState,
  updateField,
  validateField,
}) => {
  const { selectedLanguage, setSelectedLanguage } = useMultiLanguageInput();
  const isDarkMode = useAppSelector(selectIsDarkMode);

  // Validation effects
  useEffect(() => {
    const titles = contentState?.title;
    const hasValidTitle = titles?.some((t) => t.value.trim() !== '');
    const longTitle = titles?.find((t) => t.value.trim().length > 50)?.value;

    if (titles?.length === 0 || (hasValidTitle && !longTitle)) {
      validateField('title', hasValidTitle ? 'valid' : '');
    } else if (longTitle) {
      validateField('title', longTitle);
    }
  }, [contentState?.title]);

  useEffect(() => {
    const descriptions = contentState?.description;
    const hasValidDesc = descriptions?.some((d) => d.value.trim() !== '');
    const longDesc = descriptions?.find(
      (d) => d.value.trim().length > 5000
    )?.value;

    if (descriptions?.length === 0 || (hasValidDesc && !longDesc)) {
      validateField('description', hasValidDesc ? 'valid' : '');
    } else if (longDesc) {
      validateField('description', longDesc);
    }
  }, [contentState?.description]);

  useEffect(() => {
    const sharingMessages = contentState?.sharingMessage;
    const hasValidSM = sharingMessages?.some((sm) => sm.value.trim() !== '');

    if (sharingMessages?.length === 0 || hasValidSM) {
      validateField('sharingMessage', hasValidSM ? 'valid' : '');
    }
  }, [contentState?.sharingMessage]);

  useEffect(() => {
    const markdownTexts = contentState?.markdownText;
    const hasValidMD = markdownTexts?.some((md) => md.value.trim() !== '');

    if (markdownTexts?.length === 0 || hasValidMD) {
      validateField('markdownText', hasValidMD ? 'valid' : '');
    }
  }, [contentState?.markdownText]);

  useEffect(() => {
    const notifyTitles = contentState?.notificationInfo.title;
    const hasValidTitle = notifyTitles?.some((t) => t.value.trim() !== '');

    if (notifyTitles?.length === 0 || hasValidTitle) {
      validateField('notifyTitle', hasValidTitle ? 'valid' : '');
    }
  }, [contentState?.notificationInfo.title]);

  useEffect(() => {
    const notifyBodies = contentState?.notificationInfo.body;
    const hasValidBody = notifyBodies?.some((b) => b.value.trim() !== '');

    if (notifyBodies?.length === 0 || hasValidBody) {
      validateField('notifyBody', hasValidBody ? 'valid' : '');
    }
  }, [contentState?.notificationInfo.body]);

  return (
    <Card>
      <div className="mb-4">
        <Label value={t('content.selectFieldsLanguage')} className="mb-2" />
        <LanguageDropdown
          selectedLanguage={selectedLanguage}
          onSelectLanguage={setSelectedLanguage}
          isDarkMode={isDarkMode}
        />
      </div>

      {/* Title Input */}
      <MultiLanguageField
        config={{
          id: 'title-input',
          label: 'content.title',
          type: 'text',
          placeholder: 'placeholders.title',
          required: true,
          autoFocus: true,
          validation: { maxLength: 50 },
        }}
        value={contentState?.title || []}
        onChange={(value) => updateField('title' as keyof ContentState, value)}
        selectedLanguage={selectedLanguage}
        showLanguageDropdown={false}
        t={t}
        wrapInCard={false}
      />

      {/* Description Input */}
      <MultiLanguageField
        config={{
          id: 'description-input',
          label: 'content.description',
          type: 'textarea',
          placeholder: 'placeholders.description',
          required: true,
          rows: 3,
          className: 'mt-1',
          validation: { maxLength: 300 },
        }}
        value={contentState?.description || []}
        onChange={(value) =>
          updateField('description' as keyof ContentState, value)
        }
        selectedLanguage={selectedLanguage}
        showLanguageDropdown={false}
        t={t}
        wrapInCard={false}
      />

      {/* Sharing Message Input */}
      <MultiLanguageField
        config={{
          id: 'sharingMessage-input',
          label: 'content.sharingMessage',
          type: 'text',
          placeholder: 'placeholders.sharingMessage',
          required: true,
          className: 'mt-1',
        }}
        value={contentState?.sharingMessage || []}
        onChange={(value) =>
          updateField('sharingMessage' as keyof ContentState, value)
        }
        selectedLanguage={selectedLanguage}
        showLanguageDropdown={false}
        t={t}
        wrapInCard={false}
      />

      {/* Markdown Input - Custom Renderer */}
      <MultiLanguageField
        config={{
          id: 'markdownText-input',
          label: 'content.markdownText',
          type: 'custom',
          placeholder: 'placeholders.markdownText',
          required: true,
        }}
        value={contentState?.markdownText || []}
        onChange={(value) =>
          updateField('markdownText' as keyof ContentState, value)
        }
        selectedLanguage={selectedLanguage}
        showLanguageDropdown={false}
        t={t}
        wrapInCard={false}
        customRenderer={({ value, onChange, languageCode }) => (
          <MarkdownEditorWithUpload
            value={value}
            onChange={onChange}
            languageCode={languageCode}
            isDarkMode={isDarkMode}
          />
        )}
      />

      {/* Notification Title Input */}
      <MultiLanguageField
        config={{
          id: 'notify-title-input',
          label: 'content.notifyTitle',
          type: 'text',
          placeholder: 'placeholders.notifyTitle',
          required: true,
        }}
        value={contentState?.notificationInfo.title || []}
        onChange={(value) =>
          updateField('notificationInfo[title]' as keyof ContentState, value)
        }
        selectedLanguage={selectedLanguage}
        showLanguageDropdown={false}
        t={t}
        wrapInCard={false}
      />

      {/* Notification Body Input */}
      <MultiLanguageField
        config={{
          id: 'notify-body-input',
          label: 'content.notifyBody',
          type: 'textarea',
          placeholder: 'placeholders.notifyBody',
          required: true,
          rows: 3,
          className: 'mt-1',
        }}
        value={contentState?.notificationInfo.body || []}
        onChange={(value) =>
          updateField('notificationInfo[body]' as keyof ContentState, value)
        }
        selectedLanguage={selectedLanguage}
        showLanguageDropdown={false}
        t={t}
        wrapInCard={false}
      />
    </Card>
  );
};
